import { useContext } from 'react'
import { ApiContext } from '../../../contexts/api-context'
import { LoginContext } from '../../../contexts/login-context'
import { ModalContext } from '../../../contexts/modal-context'
import { UserBooking } from '../../../types/andTypes'
import { formatToReadableDate, getUserId } from '../../../utilities/utils'
import {
  BookingDate,
  BookingDetailsContainer,
  BookingTitle,
  DetailsWrapper
} from './booking-styles'
import DecisionPopup from '../../modal/popup/decision-popup/decision-popup'
import ResultPopup from '../../modal/popup/result-popup/result-popup'
import LoadingPopup from '../../modal/popup/loading-popup/loading-popup'
import { Bin, IconWrapper } from '../../user-bookings/booking-list-styles'
import bin from '../../user-bookings/assets/bin.svg'
import logAnalyticsEvent from '../../../utilities/firebase-analytics'

type ContentCardProps = {
  userBooking: UserBooking
  className?: string
}

const Booking = ({ userBooking, className }: ContentCardProps) => {
  const { businessUnits } = useContext(ApiContext)
  const displayName = businessUnits && businessUnits.find(bU => bU.id === userBooking.clubId)?.name

  const { user } = useContext(LoginContext)
  const { apiClient, userProfile } = useContext(ApiContext)
  const { setModalOpen, setModalContent } = useContext(ModalContext)

  const onDelete = (booking: UserBooking) => {
    setModalOpen(true)
    logAnalyticsEvent('delete_decision', {
      user: user?.email,
      team: userProfile?.team,
      default_location: userProfile?.businessUnit
    })
    setModalContent(
      <DecisionPopup
        header="Cancel Booking"
        decisionText="Are you sure you want to cancel your booking?"
        bookingDetails={booking}
        confirmAction={() => cancelBooking(booking.date)}
      />
    )
  }

  const cancelBooking = async (date: string) => {
    setModalContent(<LoadingPopup />)

    const startTime = new Date().getTime()
    try {
      if (!user?.email) {
        return Promise.reject(new Error('Error cancelling booking'))
      }
      await apiClient.deleteUserBooking({
        userId: getUserId(user.email),
        dateBooked: date
      })

      logAnalyticsEvent('booking_deletion_success', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        default_location: userProfile?.businessUnit
      })
      setModalContent(
        <ResultPopup headerText="Booking Cancelled" success={true}>
          <p>Your booking has been cancelled.</p>
        </ResultPopup>
      )
    } catch (error) {
      let message = ''
      logAnalyticsEvent('booking_deletion_error', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        default_location: userProfile?.businessUnit
      })

      if (
        error instanceof Error &&
        error.message.match(/The seat you are trying to cancel does not exist/)
      ) {
        message = error.message
      } else {
        message = 'Something went wrong. Please try to cancel your booking again.'
      }

      setModalContent(
        <ResultPopup headerText="Oops..Sorry" success={false}>
          <p>{message}</p>
        </ResultPopup>
      )
    }
  }

  return (
    <BookingDetailsContainer data-testid="booking-details" className={className}>
      <DetailsWrapper>
        <BookingTitle>{formatToReadableDate(userBooking.date)}</BookingTitle>
        <BookingDate>{displayName}</BookingDate>
      </DetailsWrapper>

      <IconWrapper>
        <Bin
          role="button"
          title={`delete button for ${formatToReadableDate(userBooking.date)}`}
          src={bin}
          width={'25px'}
          height={'25px'}
          onClick={() => onDelete(userBooking)}
        />
      </IconWrapper>
    </BookingDetailsContainer>
  )
}

export default Booking
